import * as Schema from "effect/Schema";
import * as Effect from "effect/Effect";

import { PortSchema } from "./baseSchemas.ts";
import { ApplicationReleaseAcceptanceIdentity } from "./applicationReleaseAcceptance.ts";
import { LocalHostSessionCredential } from "./localHostSession.ts";

/**
 * Listeners the controlled Desktop exposure lifecycle may select.
 *
 * Loopback is the default and the only listener a local-only Desktop uses.
 * `0.0.0.0` is admitted for the network-accessible exposure mode, which the
 * user opts into per machine. Tailscale Serve needs neither: it proxies from
 * the tailnet into the loopback listener, so a tailnet HTTPS route works while
 * the Environment Service stays bound to 127.0.0.1.
 */
export const DesktopBackendListenerHost = Schema.Literals(["127.0.0.1", "0.0.0.0"]);
export type DesktopBackendListenerHost = typeof DesktopBackendListenerHost.Type;

export const DesktopBackendBootstrap = Schema.Struct({
  mode: Schema.Literal("desktop"),
  clientSurface: Schema.optional(Schema.Literals(["desktop", "command-line"])),
  noBrowser: Schema.Boolean,
  port: PortSchema,
  listenerHost: DesktopBackendListenerHost.pipe(
    Schema.withDecodingDefault(Effect.succeed("127.0.0.1")),
  ),
  afarResearchHome: Schema.String,
  packaged: Schema.optional(Schema.Boolean),
  applicationRelease: Schema.optional(Schema.String),
  installedApplicationRelease: Schema.optional(Schema.String),
  lifecycleGeneration: Schema.optional(Schema.String),
  executionImagePath: Schema.optional(Schema.String),
  localLaunchClaim: Schema.optional(Schema.String),
  executionImageIdentity: Schema.optional(Schema.String),
  installationId: Schema.optional(Schema.String),
  applicationPath: Schema.optional(Schema.String),
  hostStoreReleasePaths: Schema.optional(Schema.Array(Schema.String)),
  applicationReleaseAcceptance: Schema.optional(ApplicationReleaseAcceptanceIdentity),
  localHostSession: LocalHostSessionCredential,
  /**
   * Whether this Environment Service publishes itself on the tailnet over
   * HTTPS with `tailscale serve`. The service owns the mapping's whole
   * lifetime: it is created once the listener is up and removed on shutdown.
   */
  tailscaleServeEnabled: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(false))),
  tailscaleServePort: PortSchema.pipe(Schema.withDecodingDefault(Effect.succeed(443))),
});

export type DesktopBackendBootstrap = typeof DesktopBackendBootstrap.Type;
